import { FC, Fragment } from "react";
import classNames from "classnames";
import { format } from "date-fns";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { currencyFormatterNoFractions } from "@common/helpers/currency";
import { fromIntToMonetary } from "@common/helpers/monetary";
import { UpcomingInvoiceDto } from "@common/schemas/billing.schema";
import { Button } from "@design-system/Button/Button";
import { getEnv } from "@lib/environment";
import { Loader } from "@design-system/Loader/Loader";
import { SubscriptionStatus } from "@common/billing";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useOrganizationUpcomingInvoiceQuery } from "@api/billingOrganization.api";
import styles from "./BillingPageV2InvoiceSection.module.scss";
import { EmptyState } from "designSystem/EmptyState/EmptyState";

const formatCents = (cents: number): string =>
    currencyFormatterNoFractions.format(fromIntToMonetary(cents));

interface BillingPageV2InvoiceSectionProps {
    canManageBilling: boolean;
}

export const BillingPageV2InvoiceSection: FC<
    BillingPageV2InvoiceSectionProps
> = ({ canManageBilling }) => {
    const activeOrganization = useActiveOrganization();
    const hasSubscription = !!activeOrganization?.billing?.subscriptionId;
    const isCancelled =
        activeOrganization?.billing?.subscriptionStatus ===
        SubscriptionStatus.CANCELLED;
    const { data, isLoading, isError } = useOrganizationUpcomingInvoiceQuery(
        activeOrganization?.id,
        hasSubscription,
    );

    const isInvoiceAvailable = data && !isLoading && !isError;

    return (
        <div className={styles.container}>
            <div className={styles.sectionTitle}>
                {isCancelled
                    ? "Invoice if you reactivate"
                    : "Upcoming monthly invoice"}
            </div>
            <div className={styles.cardContainer}>
                {isInvoiceAvailable && !isCancelled && (
                    <div
                        className={classNames(
                            styles.sectionHeader,
                            styles.invoiceDateHeader,
                        )}
                    >
                        <div className={styles.invoiceDateLabel}>Date</div>
                        <div className={styles.invoiceDate}>
                            {data?.periodEndsAt
                                ? format(
                                      data.periodEndsAt,
                                      fullDateFullYearFormat,
                                  )
                                : "-"}
                        </div>
                    </div>
                )}
                <InvoiceBody
                    isLoading={isLoading}
                    isError={isError}
                    data={data}
                />
                {hasSubscription && canManageBilling && (
                    <div className={styles.actions}>
                        <Button size="sm" variant="primary" asChild>
                            <a
                                href={getEnv("VITE_STRIPE_PORTAL_URL")}
                                target="_blank"
                                rel="noreferrer"
                                className="text-decoration-none"
                            >
                                View billing history
                            </a>
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

interface InvoiceBodyProps {
    isLoading: boolean;
    isError: boolean;
    data: UpcomingInvoiceDto | undefined;
}

const InvoiceBody: FC<InvoiceBodyProps> = ({ isLoading, isError, data }) => {
    if (isLoading) {
        return <Loader />;
    }

    if (isError) {
        return (
            <div className={styles.invoiceTable}>
                <div className={styles.invoiceEmpty}>
                    Upcoming invoice preview is unavailable.
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={styles.invoiceTable}>
                <div className={styles.invoiceTableHeader}>Item</div>
                <div
                    className={classNames(
                        styles.invoiceTableHeader,
                        styles.invoiceTableNumeric,
                    )}
                >
                    Quantity
                </div>
                <div
                    className={classNames(
                        styles.invoiceTableHeader,
                        styles.invoiceTableNumeric,
                    )}
                >
                    Total
                </div>
                {!data && (
                    <div className={styles.emptyStateContainer}>
                        <EmptyState header="No upcoming invoice" />
                    </div>
                )}
                {data?.items.map((item) => (
                    <Fragment key={item.key}>
                        <div className={styles.invoiceTableItem}>
                            {item.label}
                        </div>
                        <div className={styles.invoiceTableNumeric}>
                            {item.quantity}
                        </div>
                        <div
                            className={classNames(
                                styles.invoiceTableNumeric,
                                styles.total,
                            )}
                        >
                            {formatCents(item.totalCents)}
                        </div>
                    </Fragment>
                ))}
                {data?.items.length === 0 && (
                    <div className={styles.emptyStateContainer}>
                        <EmptyState header="No items" />
                    </div>
                )}
            </div>
            {data && (
                <>
                    <div className={styles.invoiceTableTotal}>
                        <div>Estimated total</div>
                        <div className={styles.total}>
                            {formatCents(data.totalCents)}
                        </div>
                    </div>
                    {data.creditsTotalGrantedCents > 0 && (
                        <div className={styles.invoiceTableTotal}>
                            <div>Credits remaining</div>
                            <div className={styles.total}>
                                {formatCents(data.creditsBalanceCents)}
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};
